import { generateGuid, getTimestamp } from '@bingads-webui-universal/primitive-utilities';
import {
  createFetchInstrumentationLoggers,
  createFetchTelemetryLifecycle,
  getLegacyFetchResponseLogMetadata,
} from '@bingads-webui-campaign-react/http-core';
import type {
  CampaignFetchOptions,
  CampaignFetchRequestOptions,
  CampaignFetchResponseLike,
  CampaignInstrumentation,
  CampaignServerContextLike,
} from './types';

export function getDefaultUrl(serverContext: CampaignServerContextLike): string {
  const { CmAPI = '' } = serverContext.ServicesEndpoints;
  return CmAPI || '';
}

const instrumentationLoggers = createFetchInstrumentationLoggers<CampaignInstrumentation>({
  scenarioName: 'CampaignFetch',
  getAjaxScenario: instrumentation => instrumentation.ajaxStubScenario,
  getRequestScenario: instrumentation => instrumentation.CampaignFetch,
  addScenario: (instrumentation, schema) => instrumentation.addScenario?.(schema),
});

export function getDefaultOptions(
  serverContext: CampaignServerContextLike,
  instrumentation?: CampaignInstrumentation
): CampaignFetchOptions {
  const { Id: aid, AdvertiserCustomerID: cid } = serverContext.CurrentAccount!;
  const { CmAPI = '' } = serverContext.ServicesEndpoints;
  const { hostname } = new URL(CmAPI || '/', typeof window === 'undefined' ? 'http://localhost' : window.location.href);
  const isOnebox = hostname === 'app1.localhost.ccui.devdev.bingads.microsoft.com' ||
    /(^|\.)redmond\.corp\.microsoft\.com$/i.test(hostname);

  const ajaxStubLogger = instrumentationLoggers.getAjaxStubLogger(instrumentation);
  const { requestLogger } = instrumentationLoggers.getRequestPayloadLogger(instrumentation);
  const { beforeFetch, afterFetch } = createFetchTelemetryLifecycle({
    ajaxStubLogger,
    requestLogger,
    getTimestamp,
    getResponseLogMetadata: getLegacyFetchResponseLogMetadata,
  });

  const finalOptions = {
    headers: {
      'Content-Type': 'application/json',
      lcid: serverContext.CurrentUser!.Locale!,
      'x-ms-clientapplication': 'bingadsweb',
      'x-ms-applicationname': 'bingadsweb',
      'x-ms-pagetrackingid': serverContext.PageTrackingId!,
      Authorization: `CCMTSmallToken ${serverContext.Token}`,
      CustomerAccountId: aid!,
      CustomerId: cid!,
      ...(isOnebox? { DeveloperToken: '1L9L5BYH550' }: {}),
    },
    cachePolicy: 'cache-first',
  };

  const defaultOptions = {
    ...finalOptions,
    interceptors: {
      request: ({
        route,
        url,
        options,
      }: { route: string; url: string; options: CampaignFetchRequestOptions }) => {
        const reqId = generateGuid();
        // eslint-disable-next-line no-param-reassign
        options.headers['x-ms-requestid'] = reqId;
        // eslint-disable-next-line no-param-reassign
        options.headers['Request-Id'] = reqId;
        // eslint-disable-next-line no-param-reassign
        options.headers['x-ms-trackingid'] = reqId;
        beforeFetch(options, url, route);
        return options;
      },
      response: ({
        response,
      }: { response: CampaignFetchResponseLike }) => {
        afterFetch(response);
        return response;
      },
    },
  };

  return defaultOptions;
}
